import { StatusChip, type StatusTone } from "@prisma/ui";
import {
  BadgeCheck,
  Ban,
  CircleDot,
  Clapperboard,
  Eye,
  HelpCircle,
  Inbox,
  PackageCheck,
  PenLine,
  Sparkles,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** Status chips (decision O7; U-05): in-flight states use the screenshot accents — production
 *  violet, review magenta, editing cyan, planning blue — and approved/due/rejected use functional
 *  colours. Always icon + label, never colour alone. Labels are catalog keys. */
const REQUEST: Record<string, { tone: StatusTone; icon: ReactNode }> = {
  SUBMITTED: { tone: "neutral", icon: <Inbox className="size-3.5" /> },
  RESUBMITTED: { tone: "neutral", icon: <Inbox className="size-3.5" /> },
  CP_REVIEW: { tone: "planning", icon: <Eye className="size-3.5" /> },
  NEED_CLARIFICATION: { tone: "warning", icon: <HelpCircle className="size-3.5" /> },
  REJECTED: { tone: "danger", icon: <Ban className="size-3.5" /> },
  APPROVED_FOR_STRATEGY: { tone: "planning", icon: <Sparkles className="size-3.5" /> },
  STRATEGY_IN_PROGRESS: { tone: "planning", icon: <Sparkles className="size-3.5" /> },
  CREATIVE_PLAN_READY: { tone: "planning", icon: <PenLine className="size-3.5" /> },
  APPROVED_FOR_PRODUCTION: { tone: "production", icon: <Clapperboard className="size-3.5" /> },
  IN_PRODUCTION: { tone: "production", icon: <Clapperboard className="size-3.5" /> },
  REVIEW: { tone: "review", icon: <Eye className="size-3.5" /> },
  APPROVED: { tone: "success", icon: <BadgeCheck className="size-3.5" /> },
  DELIVERED: { tone: "success", icon: <PackageCheck className="size-3.5" /> },
};

export function RequestStatusChip({ status }: { status: string }) {
  const { t } = useTranslation();
  const s = REQUEST[status] ?? { tone: "neutral" as StatusTone, icon: <CircleDot className="size-3.5" /> };
  return <StatusChip tone={s.tone} icon={s.icon} label={t(`status.creative_request.${status.toLowerCase()}`)} />;
}
